import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs,
  Tab,
  Box,
  MenuItem,
  TextField,
  Button,
  Grid,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  InputAdornment
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import moment from 'moment';
import 'moment/locale/fr';
import { useNavigate } from 'react-router-dom';
import { 
  EventNote, People, CalendarToday, Euro, 
  NavigateBefore, NavigateNext, Today,
  FilterList, Category, Group, Event, MonetizationOn
} from '@mui/icons-material';
import { collection, getDocs, doc, runTransaction, deleteDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { Public as PublicIcon } from '@mui/icons-material';
import {
  PUBLIC_STATUS, ACTIVE_ORDER_STATUSES, ticketSalesSummary, minTicketPrice,
} from '../utils/publicEvents';

// Constante pour permettre d'aligner avec la structure écrite par l'app mobile
const PARTICIPANTS_SUBCOLLECTION = 'participants';
// Commandes de billets (workshops / stages), écrites par les Cloud Functions
const ORDERS_SUBCOLLECTION = 'orders';

// Couleurs du calendrier : violet MKD pour les événements publiés sur le site
const PUBLIC_EVENT_COLOR = '#7C3AED';
const INTERNAL_EVENT_COLOR = '#1976d2';

moment.locale('fr');
const localizer = momentLocalizer(moment);

const months = moment.months();

export default function Events() {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success' | 'error' | 'warning' | 'info'
  });

  const handleParticipation = async (eventId, isParticipating) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        setSnackbar({
          open: true,
          message: "Veuillez vous connecter pour participer",
          severity: 'warning'
        });
        return;
      }

      await runTransaction(db, async (transaction) => {
        const eventDoc = await transaction.get(eventRef);
        if (!eventDoc.exists()) throw "Document does not exist!";
        
        const eventData = eventDoc.data();
        const selectedStudents = eventData.selectedStudents || [];
        const newSelectedStudents = isParticipating
          ? selectedStudents.filter(id => id !== currentUser.uid)
          : [...selectedStudents, currentUser.uid];
        
        transaction.update(eventRef, {
          selectedStudents: newSelectedStudents,
          participantsCount: isParticipating 
            ? (eventData.participantsCount || 0) - 1 
            : (eventData.participantsCount || 0) + 1
        });
      });

      // Rafraîchir les données
      const eventsSnapshot = await getDocs(collection(db, 'events'));
      const eventsData = eventsSnapshot.docs.map(doc => {
        const eventData = doc.data();
        const startDate = eventData.startDate?.toDate();
        const endDate = eventData.endDate?.toDate();

        return {
          id: doc.id,
          ...eventData,
          startDate,
          endDate,
          rawStartDate: startDate,
          rawEndDate: endDate,
        };
      });

      setEvents(eventsData);
      setFilteredEvents(eventsData);

      setSnackbar({
        open: true,
        message: isParticipating 
          ? "Vous êtes désormais désinscrit de cet événement" 
          : "Vous êtes maintenant inscrit à cet événement",
        severity: 'success'
      });

    } catch (error) {
      console.error("Erreur de participation :", error);
      setSnackbar({
        open: true,
        message: "Erreur lors de la mise à jour de la participation",
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };
  const [tab, setTab] = useState(0);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    audience: '',
    month: '',
    price: '',
    visibility: '',
  });

  const resetFilters = () => setFilters({
    category: '',
    audience: '',
    month: '',
    price: '',
    visibility: '',
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Récupération des données
  useEffect(() => {
    const fetchData = async () => {
  try {
    setLoading(true);
    // Récupérer les événements
    const eventsSnapshot = await getDocs(collection(db, 'events'));
    // Récupérer les cours
    const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
    const coursesData = coursesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setCourses(coursesData);

    const eventsDataRaw = eventsSnapshot.docs.map(docSnap => {
      const eventData = docSnap.data();
      const startDate = eventData.startDate?.toDate();
      const endDate = eventData.endDate?.toDate();

      // Correction importante pour les dates
      if (startDate && endDate) {
        if (eventData.allDay) {
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(23, 59, 59, 999);
        }
      }

        // Convertir le prix en nombre
  const price = Number(eventData.price) || 0;

      // Formater les heures si elles existent
      const startTime = eventData.startTime || 
                       (startDate ? moment(startDate).format('HH:mm') : null);
      const endTime = eventData.endTime || 
                     (endDate ? moment(endDate).format('HH:mm') : null);

      // Trouver le cours associé si public spécifique
      const linkedCourse = !eventData.isForAllStudents && eventData.selectedCourse 
        ? coursesData.find(c => c.id === eventData.selectedCourse)
        : null;

      return {
        id: docSnap.id,
        ...eventData,
        startDate,
        endDate,
        startTime,
        endTime,
        rawStartDate: startDate,
        rawEndDate: endDate,
        price: eventData.price || 0,
        isForAllStudents: eventData.isForAllStudents || false,
        selectedCourse: eventData.selectedCourse || null,
        linkedCourseTitle: linkedCourse ? linkedCourse.title : null
      };
    });

    // Récupérer le nombre de participants depuis la sous-collection
    const eventsData = await Promise.all(
      eventsDataRaw.map(async (evt) => {
        try {
          const sub = await getDocs(collection(db, 'events', evt.id, PARTICIPANTS_SUBCOLLECTION));
          return { ...evt, participantsCount: sub.size };
        } catch (e) {
          console.error('Erreur lecture participants pour', evt.id, e);
          return { ...evt, participantsCount: 0 };
        }
      })
    );

    setEvents(eventsData);
    setFilteredEvents(eventsData);
  } catch (error) {
    console.error("Erreur lors de la récupération des données:", error);
  } finally {
    setLoading(false);
  }
};
    fetchData();
  }, []);

  // Filtrage des événements
  useEffect(() => {
    let result = [...events];

    if (filters.category) {
      result = result.filter(e => e.category === filters.category);
    }
    
    if (filters.audience) {
      if (filters.audience === 'all') {
        result = result.filter(e => e.isForAllStudents);
      } else if (filters.audience === 'specific') {
        result = result.filter(e => !e.isForAllStudents);
      }
    }
    
    if (filters.month !== '') {
      result = result.filter(e => 
        e.startDate?.getMonth() === parseInt(filters.month)
      );
    }
    
    if (filters.price === 'gratuit') {
      result = result.filter(e => Number(e.price) === 0);
    } else if (filters.price === 'payant') {
      result = result.filter(e => Number(e.price) > 0);
    }

    if (filters.visibility === 'site') {
      result = result.filter(e => e.isPublic === true);
    } else if (filters.visibility === 'internal') {
      result = result.filter(e => e.isPublic !== true);
    }

    setFilteredEvents(result);
    
    if (filters.month !== '' && tab === 0) {
      const newDate = new Date();
      newDate.setMonth(parseInt(filters.month));
      setCurrentDate(newDate);
    }
  }, [filters, events, tab]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const transformedEvents = filteredEvents.map(event => ({
    ...event,
    title: event.title,
    start: event.rawStartDate || new Date(),
    end: event.rawEndDate || event.rawStartDate || new Date(),
    allDay: event.allDay || false
  }));

  // Navigation dans le calendrier
  const navigateToPrevious = useCallback(() => {
    if (view === 'month') {
      setCurrentDate(moment(currentDate).subtract(1, 'months').toDate());
    } else if (view === 'week') {
      setCurrentDate(moment(currentDate).subtract(1, 'weeks').toDate());
    } else {
      setCurrentDate(moment(currentDate).subtract(1, 'days').toDate());
    }
  }, [currentDate, view]);

  const navigateToNext = useCallback(() => {
    if (view === 'month') {
      setCurrentDate(moment(currentDate).add(1, 'months').toDate());
    } else if (view === 'week') {
      setCurrentDate(moment(currentDate).add(1, 'weeks').toDate());
    } else {
      setCurrentDate(moment(currentDate).add(1, 'days').toDate());
    }
  }, [currentDate, view]);

  const navigateToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const changeView = useCallback((newView) => {
    setView(newView);
  }, []);

  // Colonnes pour la DataGrid
  const columns = [
  {
    field: 'title',
    headerName: 'Titre',
    flex: 1,
    minWidth: 200,
    renderCell: (params) => (
      <Button
        variant="text"
        onClick={() => navigate(`/events/${params.row.id}`)}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {params.row.title || 'Événement sans titre'}
      </Button>
    )
  },
  {
    field: 'site',
    headerName: 'Site',
    width: 190,
    valueGetter: (params) => {
      if (!params || !params.row) return '';
      return params.row.isPublic ? (PUBLIC_STATUS[params.row.status]?.label || 'Brouillon') : 'Interne';
    },
    renderCell: (params) => {
      const row = params?.row;
      if (!row) return '—';
      if (!row.isPublic) {
        return <Chip label="Interne" size="small" variant="outlined" />;
      }
      const meta = PUBLIC_STATUS[row.status] || PUBLIC_STATUS.draft;
      const sales = ticketSalesSummary(row);
      return (
        <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center" height="100%" gap={0.25}>
          <Chip icon={<PublicIcon />} label={meta.label} color={meta.color} size="small" />
          <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
            {sales.capacity === null ? `${sales.sold} vendue(s)` : `${sales.sold}/${sales.capacity} vendue(s)`}
            {sales.reserved ? ` · ${sales.reserved} en attente` : ''}
          </Typography>
        </Box>
      );
    },
  },
  { field: 'category', headerName: 'Catégorie', flex: 1, minWidth: 150 },
  { 
    field: 'audience', 
    headerName: 'Public', 
    flex: 1, 
    minWidth: 180,
    valueGetter: (params) => {
      if (!params || !params.row) return '—';
      if (params.row.isForAllStudents) return 'Tous les élèves';
      return params.row.linkedCourseTitle || 'Public spécifique';
    },
    renderCell: (params) => {
      if (!params || !params.row) return '—';
      if (params.row.isForAllStudents) return (
        <Chip 
          label="Tous les élèves" 
          color="success" 
          size="small"
          icon={<Group />}
        />
      );
      return params.row.linkedCourseTitle ? (
        <Tooltip title={params.row.linkedCourseTitle}>
          <Chip 
            label={params.row.linkedCourseTitle} 
            color="info" 
            size="small"
            icon={<Event />}
          />
        </Tooltip>
      ) : (
        <Chip 
          label="Public spécifique" 
          color="warning" 
          size="small"
        />
      );
    }
  },
 {
  field: 'startDate',
  headerName: 'Début',
  type: 'dateTime',
  width: 220,
  // Toujours vérifier params et params.row
  valueGetter: (params) => {
    if (!params || !params.row) return null;
    const d = params.row.startDate || params.row.rawStartDate;
    return d ? new Date(d) : null;
  },
  renderCell: (params) => {
    if (!params || !params.row) return '—';
    const row = params.row;
    const d = params.value;
    if (!d) return '—';
    const dateStr = moment(d).format('DD/MM/YYYY');
    if (row.allDay) return dateStr;
    const timeStr = row.startTime || moment(d).format('HH:mm');
    return `${dateStr} à ${timeStr}`;
  },
  sortComparator: (v1, v2) => {
    const t1 = v1 instanceof Date ? v1.getTime() : 0;
    const t2 = v2 instanceof Date ? v2.getTime() : 0;
    return t1 - t2;
  },
},
{
  field: 'endDate',
  headerName: 'Fin',
  type: 'dateTime',
  width: 220,
  valueGetter: (params) => {
    if (!params || !params.row) return null;
    const d = params.row.endDate || params.row.rawEndDate;
    return d ? new Date(d) : null;
  },
  renderCell: (params) => {
    if (!params || !params.row) return '—';
    const row = params.row;
    const d = params.value;
    if (!d) return '—';
    const dateStr = moment(d).format('DD/MM/YYYY');
    if (row.allDay) return dateStr;
    const timeStr = row.endTime || moment(d).format('HH:mm');
    return `${dateStr} à ${timeStr}`;
  },
  sortComparator: (v1, v2) => {
    const t1 = v1 instanceof Date ? v1.getTime() : 0;
    const t2 = v2 instanceof Date ? v2.getTime() : 0;
    return t1 - t2;
  },
},
  {
    field: 'price',
  headerName: 'Prix',
  width: 120,
  valueGetter: (params) => {
    if (!params || !params.row) return 0;
    // Convertir en nombre si ce n'est pas déjà le cas
    const price = Number(params.row.price) || 0;
    return price;
  },
  renderCell: (params) => {
    // Événement public : prix d'appel = ticket actif le moins cher
    const isPublic = params.row.isPublic === true;
    const fromPrice = isPublic ? minTicketPrice(params.row) : null;
    // S'assurer que le prix est bien un nombre
    const price = isPublic ? (fromPrice ?? 0) : (Number(params.row.price) || 0);
    return (
      <Box display="flex" alignItems="center">
        {price > 0 && <Euro fontSize="small" sx={{ mr: 0.5 }} />}
        <Chip
          label={price === 0 ? "Gratuit" : `${isPublic ? 'dès ' : ''}${price.toFixed(2)} €`}
          color={price === 0 ? "success" : "primary"} 
          size="small"
          sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
        />
      </Box>
      );
    },
  },
  {
    field: 'participantsCount',
    headerName: 'Participants',
    width: 150,
    renderCell: (params) => (
      <Button
        variant="outlined"
        size="small"
        onClick={() => navigate(`/events/${params.row.id}`)}
      >
        {params.row.participantsCount ?? 0}
      </Button>
    ),
    valueGetter: (params) => params.row?.participantsCount ?? 0,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 200,
    renderCell: (params) => (
      <Box display="flex" gap={1}>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/events/${params.row.id}/edit`)}
        >
          Modifier
        </Button>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => handleDeleteEvent(params.row.id)}
        >
          Supprimer
        </Button>
      </Box>
    ),
  },
];

  const handleDeleteEvent = async (eventId) => {
    try {
      // 0) Billetterie : un événement avec des commandes payées ou en cours
      //    de paiement ne se supprime pas (justificatifs de paiement,
      //    places réservées). On propose le statut « Annulé » à la place.
      const ordersSnap = await getDocs(collection(db, 'events', eventId, ORDERS_SUBCOLLECTION));
      const activeOrders = ordersSnap.docs.filter((d) => ACTIVE_ORDER_STATUSES.includes(d.data().status));
      if (activeOrders.length > 0) {
        const paid = activeOrders.filter((d) => d.data().status === 'paid').length;
        setSnackbar({
          open: true,
          severity: 'warning',
          message: `Suppression impossible : ${paid} commande(s) payée(s) et ${activeOrders.length - paid} en attente de paiement. `
            + 'Passez l\'événement en statut « Annulé » (Modifier → Publication sur le site).',
        });
        return;
      }

      if (!window.confirm('Supprimer cet événement ? Les inscriptions associées seront également supprimées.')) return;

      // 1) supprimer les commandes abandonnées / annulées (seules autorisées
      //    par les règles), puis les participants
      await Promise.all(ordersSnap.docs.map((d) => deleteDoc(d.ref)));

      const partsSnap = await getDocs(collection(db, 'events', eventId, PARTICIPANTS_SUBCOLLECTION));
      const batchOps = partsSnap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(batchOps);

      // 2) supprimer le doc de l'événement
      await deleteDoc(doc(db, 'events', eventId));

      // 3) rafraîchir la liste
      const refreshed = events.filter(e => e.id !== eventId);
      setEvents(refreshed);
      setFilteredEvents(refreshed);

      setSnackbar({ open: true, message: 'Événement supprimé', severity: 'success' });
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
    }
  };

  // Options pour les filtres
  const categories = [...new Set(events.map(e => e?.category).filter(Boolean))];
  const audiences = [
    { value: '', label: 'Tous publics' },
    { value: 'all', label: 'Tous les élèves' },
    { value: 'specific', label: 'Public spécifique' }
  ];

  // Rendu personnalisé de l'événement dans le calendrier
  const EventComponent = ({ event }) => {
    const courseTitle = event?.linkedCourseTitle;

    return (
      <div style={{
        backgroundColor: event?.isPublic ? PUBLIC_EVENT_COLOR : INTERNAL_EVENT_COLOR,
        color: 'white',
        padding: '5px',
        borderRadius: '4px',
        fontSize: '0.85rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        borderLeft: `4px solid ${event?.categoryColor ? `#${event.categoryColor.toString(16).padStart(6, '0')}` : '#ff5722'}`,
      }}>
        <strong>{event?.isPublic ? '🌐 ' : ''}{event?.title || 'Événement sans titre'}</strong>
        {event?.startDate && !event?.allDay && (
          <div>{moment(event.startDate).format('HH:mm')}{event?.endDate && ` - ${moment(event.endDate).format('HH:mm')}`}</div>
        )}
        {Number(event?.price) > 0 && <div>{event?.isPublic ? 'dès ' : ''}{Number(event.price).toFixed(2)} €</div>}
        {courseTitle && (
          <div style={{ fontSize: '0.7rem', marginTop: '2px' }}>{courseTitle}</div>
        )}
      </div>
    );
  };

  // Toolbar personnalisée pour le calendrier
  const CustomToolbar = (toolbar) => {
    return (
      <Box display="flex" justifyContent="space-between" alignItems="center" p={1} mb={1} sx={{ backgroundColor: '#f5f7fa', borderRadius: 1 }}>
        <Box display="flex" alignItems="center">
          <Tooltip title="Mois précédent">
            <IconButton onClick={navigateToPrevious}>
              <NavigateBefore />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Aujourd'hui">
            <IconButton onClick={navigateToToday}>
              <Today />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Mois suivant">
            <IconButton onClick={navigateToNext}>
              <NavigateNext />
            </IconButton>
          </Tooltip>
          
          <Typography variant="h6" ml={2} fontWeight="bold">
            {moment(toolbar.date).format('MMMM YYYY')}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1}>
          <Button 
            variant={view === 'month' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => changeView('month')}
            sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
          >
            Mois
          </Button>
          <Button 
            variant={view === 'week' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => changeView('week')}
            sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
          >
            Semaine
          </Button>
          <Button 
            variant={view === 'day' ? 'contained' : 'outlined'} 
            size="small"
            onClick={() => changeView('day')}
            sx={{ fontSize: '0.8rem', fontWeight: 'bold' }}
          >
            Jour
          </Button>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box p={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography variant="h5">Chargement des événements...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          Calendrier des Événements
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/events/new')}
          startIcon={<EventNote />}
          sx={{ 
            backgroundColor: '#4caf50',
            '&:hover': { backgroundColor: '#388e3c' },
            fontWeight: 'bold',
            boxShadow: 2,
            fontSize: '1rem',
            py: 1.2,
            px: 2.5
          }}
        >
          Ajouter un événement
        </Button>
      </Box>

      {/* Section Filtres */}
      <Paper elevation={3} sx={{ backgroundColor: 'white', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2} gap={1}>
          <FilterList color="primary" fontSize="medium" />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Filtres
          </Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md>
            <TextField
              select
              fullWidth
              label="Catégorie"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Category fontSize="medium" color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem' }
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" sx={{ fontSize: '1rem' }}>Toutes catégories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category} sx={{ fontSize: '1rem' }}>{category}</MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md>
            <TextField
              select
              fullWidth
              label="Public"
              value={filters.audience}
              onChange={(e) => handleFilterChange('audience', e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Group fontSize="medium" color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem' }
              }}
              sx={{ minWidth: 200 }}
            >
              {audiences.map((audience) => (
                <MenuItem key={audience.value} value={audience.value} sx={{ fontSize: '1rem' }}>
                  {audience.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md>
            <TextField
              select
              fullWidth
              label="Mois"
              value={filters.month}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarToday fontSize="medium" color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem' }
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" sx={{ fontSize: '1rem' }}>Tous les mois</MenuItem>
              {months.map((month, index) => (
                <MenuItem key={month} value={index} sx={{ fontSize: '1rem' }}>{month}</MenuItem>
              ))}
            </TextField>
          </Grid>
          
          <Grid item xs={12} sm={6} md>
            <TextField
              select
              fullWidth
              label="Prix"
              value={filters.price}
              onChange={(e) => handleFilterChange('price', e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MonetizationOn fontSize="medium" color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem' }
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" sx={{ fontSize: '1rem' }}>Tous les prix</MenuItem>
              <MenuItem value="gratuit" sx={{ fontSize: '1rem' }}>Gratuit</MenuItem>
              <MenuItem value="payant" sx={{ fontSize: '1rem' }}>Payant</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6} md>
            <TextField
              select
              fullWidth
              label="Visibilité"
              value={filters.visibility}
              onChange={(e) => handleFilterChange('visibility', e.target.value)}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PublicIcon fontSize="medium" color="primary" />
                  </InputAdornment>
                ),
                sx: { fontSize: '1rem' }
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" sx={{ fontSize: '1rem' }}>Tous</MenuItem>
              <MenuItem value="site" sx={{ fontSize: '1rem' }}>Publiés sur le site</MenuItem>
              <MenuItem value="internal" sx={{ fontSize: '1rem' }}>Internes</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Section Affichage */}
      <Paper elevation={3} sx={{ backgroundColor: 'white', borderRadius: 2, p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Tabs 
            value={tab} 
            onChange={(e, val) => setTab(val)}
            indicatorColor="primary"
            textColor="primary"
            sx={{ minHeight: 'auto' }}
          >
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1} px={2} py={1}>
                  <CalendarToday fontSize="medium" />
                  <Typography fontWeight="bold" fontSize="1rem">Vue Calendrier</Typography>
                </Box>
              } 
              sx={{ minHeight: 'auto', py: 1 }} 
            />
            <Tab 
              label={
                <Box display="flex" alignItems="center" gap={1} px={2} py={1}>
                  <Event fontSize="medium" />
                  <Typography fontWeight="bold" fontSize="1rem">Vue Liste</Typography>
                </Box>
              } 
              sx={{ minHeight: 'auto', py: 1 }} 
            />
          </Tabs>
          
          <Box display="flex" gap={2} alignItems="center">
            <Chip 
              label={`${filteredEvents.length} événement${filteredEvents.length !== 1 ? 's' : ''}`} 
              color="primary" 
              variant="outlined"
              sx={{ fontSize: '1rem', fontWeight: 'bold', py: 1.5, px: 2 }}
            />
            <Button 
              variant="outlined" 
              onClick={resetFilters}
              size="medium"
              startIcon={<FilterList fontSize="medium" />}
              sx={{ fontSize: '1rem', fontWeight: 'bold', py: 1.2, px: 2.5 }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Box>

        {tab === 0 ? (
          <Box>
            <Calendar
              localizer={localizer}
              events={transformedEvents}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              style={{ height: 600 }}
              date={currentDate}
              view={view}
              onView={setView}
              onNavigate={setCurrentDate}
              messages={{
                today: "Aujourd'hui",
                previous: 'Précédent',
                next: 'Suivant',
                month: 'Mois',
                week: 'Semaine',
                day: 'Jour',
                agenda: 'Agenda',
                date: 'Date',
                time: 'Heure',
                event: 'Événement',
              }}
              components={{
                event: EventComponent,
                toolbar: CustomToolbar
              }}
              eventPropGetter={(event) => ({
                style: {
                  backgroundColor: event?.isPublic ? PUBLIC_EVENT_COLOR : INTERNAL_EVENT_COLOR,
                  borderRadius: '4px',
                  border: 'none',
                  opacity: 0.8,
                },
              })}
            />
          </Box>
        ) : (
          <Box height={650}>
            <DataGrid
              rows={filteredEvents}
              columns={columns}
              getRowId={(row) => row.id}
              disableRowSelectionOnClick
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              sx={{
                fontSize: '1rem',
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f1f5f9',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '1rem',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: '#f8fafc',
                  },
                },
              }}
              loading={loading}
            />
          </Box>
        )}
      </Paper>
      
      {!loading && filteredEvents.length === 0 && (
        <Box textAlign="center" mt={4} p={4} sx={{ backgroundColor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h5" color="textSecondary">
            Aucun événement trouvé avec les filtres sélectionnés
          </Typography>
          <Button 
            variant="outlined" 
            onClick={resetFilters}
            sx={{ mt: 2, fontSize: '1rem', py: 1.2, px: 2.5 }}
            startIcon={<FilterList fontSize="large" />}
          >
            Réinitialiser les filtres
          </Button>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}